import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { handleApiError } from '../../utils/errorHandler';
import { Mail, Lightbulb, Check } from 'lucide-react';
import Modal from '../Modal';
import StaffPicker from '../common/StaffPicker';
import { appointmentsApi, CreateAppointmentDto, AppointmentType, MeetingType, EntityType } from '../../services/appointmentsService';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;

const RequiredStar = styled.span`
  color: var(--error-color);
  margin-right: 4px;
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }

  &:disabled {
    background: var(--background-secondary);
    cursor: not-allowed;
  }
`;

const Select = styled.select<{ hasError?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  transition: all 0.2s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: var(--text-primary);

  input[type="radio"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: var(--background-secondary);
  border-radius: var(--border-radius-md);
  border: 1px solid var(--border-color);
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--text-primary);
  cursor: pointer;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const SubGroup = styled.div`
  padding-right: 26px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SmallInput = styled(Input)`
  width: 80px;
`;

const ErrorText = styled.span`
  color: var(--error-color);
  font-size: 13px;
  margin-top: 4px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;

  @media (max-width: 767px) {
    flex-direction: column-reverse;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;
    border: 1px solid var(--primary-color);
    &:hover:not(:disabled) { background: transparent; color: var(--primary-color); }
  ` : `
    background: var(--surface-color);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    &:hover:not(:disabled) { background: var(--hover-background); }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InfoBox = styled.div`
  padding: 12px;
  background: var(--primary-light);
  border: 1px solid var(--primary-color);
  border-radius: var(--border-radius-md);
  font-size: 13px;
  color: var(--text-primary);
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const InfoIcon = styled.span`
  color: var(--primary-color);
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const ButtonContent = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CheckboxIcon = styled.span`
  display: flex;
  align-items: center;
  color: var(--primary-color);
`;

interface AddAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultAssignedUserId: number;
  entityType: EntityType;
  entityId: number;
  entityName?: string;
}

const AddAppointmentModal: React.FC<AddAppointmentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultAssignedUserId,
  entityType,
  entityId,
  entityName
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<CreateAppointmentDto>({
    entityType,
    entityId,
    assignedUserId: defaultAssignedUserId,
    appointmentType: AppointmentType.ExistingAppointment,
    doctorName: '',
    appointmentDateTime: '',
    phoneForScheduling: '',
    address: '',
    instructionsForClient: '',
    meetingType: MeetingType.Frontal,
    virtualMeetingLink: '',
    sendEmailInvitation: false,
    clientMedicalInfoId: undefined,
    createReminderBeforeAppointment: false,
    daysBeforeToRemind: 2,
    createReminderAfterAppointment: false,
    daysAfterToRemind: 3
  });

  useEffect(() => {
    if (isOpen) {
      // Set default datetime to next hour + sync linked entity
      const now = new Date();
      now.setHours(now.getHours() + 1, 0, 0, 0);
      const defaultDateTime = now.toISOString().slice(0, 16);
      setFormData(prev => ({ ...prev, entityType, entityId, appointmentDateTime: defaultDateTime }));
    }
  }, [isOpen, entityType, entityId]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.doctorName.trim()) {
      newErrors.doctorName = 'שם הרופא הוא שדה חובה';
    }

    if (formData.appointmentType === AppointmentType.ExistingAppointment && !formData.appointmentDateTime) {
      newErrors.appointmentDateTime = 'תאריך ושעה הם שדות חובה לפגישה קיימת';
    }

    if (formData.meetingType === MeetingType.Virtual && !formData.virtualMeetingLink?.trim()) {
      newErrors.virtualMeetingLink = 'קישור Zoom/Teams הוא שדה חובה לפגישה וירטואלית';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);
      await appointmentsApi.createAppointment(formData);
      onSuccess();
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה ביצירת הפגישה'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof CreateAppointmentDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="הוספת פגישה חדשה"
      width="600px"
    >
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>
            <RequiredStar>*</RequiredStar>
            סוג הפגישה
          </Label>
          <Select
            value={formData.appointmentType}
            onChange={(e) => handleChange('appointmentType', parseInt(e.target.value))}
          >
            <option value={AppointmentType.ExistingAppointment}>פגישה קיימת (כבר קבוע תאריך)</option>
            <option value={AppointmentType.NeedToSchedule}>צריך לתאם פגישה</option>
          </Select>
        </FormGroup>

        {entityName && (
          <FormGroup>
            <Label>{entityType === EntityType.Lead ? 'ליד' : 'לקוח'}</Label>
            <Input type="text" value={entityName} disabled />
          </FormGroup>
        )}

        <StaffPicker
          label="עובד מטפל"
          value={formData.assignedUserId}
          onChange={(userId) => handleChange('assignedUserId', userId)}
        />

        <FormGroup>
          <Label>
            <RequiredStar>*</RequiredStar>
            שם הרופא / מומחה
          </Label>
          <Input
            type="text"
            value={formData.doctorName}
            onChange={(e) => handleChange('doctorName', e.target.value)}
            placeholder="למשל: ד״ר יוסי כהן - קרדיולוג"
            hasError={!!errors.doctorName}
          />
          {errors.doctorName && <ErrorText>{errors.doctorName}</ErrorText>}
        </FormGroup>

        {formData.appointmentType === AppointmentType.ExistingAppointment && (
          <FormGroup>
            <Label>
              <RequiredStar>*</RequiredStar>
              תאריך ושעת הפגישה
            </Label>
            <Input
              type="datetime-local"
              value={formData.appointmentDateTime}
              onChange={(e) => handleChange('appointmentDateTime', e.target.value)}
              hasError={!!errors.appointmentDateTime}
            />
            {errors.appointmentDateTime && <ErrorText>{errors.appointmentDateTime}</ErrorText>}
          </FormGroup>
        )}

        {formData.appointmentType === AppointmentType.ExistingAppointment && (
          <FormGroup>
            <Label>סוג הפגישה</Label>
            <RadioGroup>
              <RadioLabel>
                <input
                  type="radio"
                  name="meetingType"
                  value={MeetingType.Frontal}
                  checked={formData.meetingType === MeetingType.Frontal}
                  onChange={(e) => handleChange('meetingType', parseInt(e.target.value))}
                />
                פרונטלי (פנים אל פנים)
              </RadioLabel>
              <RadioLabel>
                <input
                  type="radio"
                  name="meetingType"
                  value={MeetingType.Virtual}
                  checked={formData.meetingType === MeetingType.Virtual}
                  onChange={(e) => handleChange('meetingType', parseInt(e.target.value))}
                />
                וירטואלי (Zoom / Teams)
              </RadioLabel>
            </RadioGroup>
          </FormGroup>
        )}

        {formData.appointmentType === AppointmentType.ExistingAppointment &&
         formData.meetingType === MeetingType.Virtual && (
          <FormGroup>
            <Label>
              <RequiredStar>*</RequiredStar>
              קישור Zoom / Teams
            </Label>
            <Input
              type="url"
              value={formData.virtualMeetingLink}
              onChange={(e) => handleChange('virtualMeetingLink', e.target.value)}
              placeholder="https://zoom.us/j/123456789 או https://teams.microsoft.com/..."
              hasError={!!errors.virtualMeetingLink}
            />
            {errors.virtualMeetingLink && <ErrorText>{errors.virtualMeetingLink}</ErrorText>}
          </FormGroup>
        )}

        {formData.appointmentType === AppointmentType.ExistingAppointment &&
         formData.meetingType === MeetingType.Frontal && (
          <FormGroup>
            <Label>כתובת</Label>
            <Input
              type="text"
              value={formData.address}
              onChange={(e) => handleChange('address', e.target.value)}
              placeholder="למשל: רחוב הרצל 10, תל אביב"
            />
          </FormGroup>
        )}

        <FormGroup>
          <Label>טלפון לתיאום</Label>
          <Input
            type="tel"
            value={formData.phoneForScheduling}
            onChange={(e) => handleChange('phoneForScheduling', e.target.value)}
            placeholder="למשל: 03-1234567"
          />
        </FormGroup>

        <FormGroup>
          <Label>הוראות ללקוח</Label>
          <TextArea
            value={formData.instructionsForClient}
            onChange={(e) => handleChange('instructionsForClient', e.target.value)}
            placeholder="מה להביא לפגישה, מה לספר לרופא..."
          />
        </FormGroup>

        {formData.appointmentType === AppointmentType.ExistingAppointment && (
          <CheckboxGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.sendEmailInvitation}
                onChange={(e) => handleChange('sendEmailInvitation', e.target.checked)}
              />
              <CheckboxIcon><Mail size={16} strokeWidth={2} /></CheckboxIcon> שלח הזמנה ללקוח במייל (הלקוח יוכל לאשר או לבטל)
            </CheckboxLabel>
          </CheckboxGroup>
        )}

        <CheckboxGroup>
          <Label>תזכורות אוטומטיות</Label>

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={formData.createReminderBeforeAppointment}
              onChange={(e) => handleChange('createReminderBeforeAppointment', e.target.checked)}
            />
            צור תזכורת לפני הפגישה - לשלוח הוראות ללקוח
          </CheckboxLabel>

          {formData.createReminderBeforeAppointment && (
            <SubGroup>
              <SmallInput
                type="number"
                min="1"
                max="30"
                value={formData.daysBeforeToRemind}
                onChange={(e) => handleChange('daysBeforeToRemind', parseInt(e.target.value))}
              />
              <span>ימים לפני הפגישה</span>
            </SubGroup>
          )}

          <CheckboxLabel>
            <input
              type="checkbox"
              checked={formData.createReminderAfterAppointment}
              onChange={(e) => handleChange('createReminderAfterAppointment', e.target.checked)}
            />
            צור תזכורת אחרי הפגישה - לבקש סיכום מהלקוח
          </CheckboxLabel>

          {formData.createReminderAfterAppointment && (
            <SubGroup>
              <SmallInput
                type="number"
                min="1"
                max="30"
                value={formData.daysAfterToRemind}
                onChange={(e) => handleChange('daysAfterToRemind', parseInt(e.target.value))}
              />
              <span>ימים אחרי הפגישה</span>
            </SubGroup>
          )}
        </CheckboxGroup>

        <InfoBox>
          <InfoIcon><Lightbulb size={18} strokeWidth={2} /></InfoIcon>
          <span><strong>טיפ:</strong> המערכת תיצור תזכורות אוטומטיות עבורך לפני ואחרי הפגישה, כדי שלא תשכח לשלוח הוראות ללקוח ולבקש ממנו סיכום.</span>
        </InfoBox>

        <ButtonGroup>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            ביטול
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? 'שומר...' : <ButtonContent><Check size={16} strokeWidth={2} />צור פגישה</ButtonContent>}
          </Button>
        </ButtonGroup>
      </Form>
    </Modal>
  );
};

export default AddAppointmentModal;
